import React from 'react'
import { useAuth } from '../hooks/useSupabaseAuth'
import { useSubscription } from '../hooks/useSubscription'
import SubscriptionPackages from '../components/SubscriptionPackages'
import SubscriptionErrorBoundary from '../components/SubscriptionErrorBoundary'
import { API_CONFIG } from '../constants/apiConfig'

const SubscriptionPage = () => {
  const { user, loading: authLoading } = useAuth()
  const { loading, error, healthCheck } = useSubscription()

  // Handle auth modal opening
  const handleAuthModalOpen = () => {
    // Dispatch custom event to open auth modal
    window.dispatchEvent(new CustomEvent('openAuthModal'))
  }

  // Health check on mount
  React.useEffect(() => {
    if (API_CONFIG.DEBUG) {
      healthCheck().then(result => {
        console.log('Subscription service health check:', result)
      })
    }
  }, [])

  // Show loading state during auth check
  if (authLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-text-tertiary">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center px-4">
        <div className="text-center max-w-md w-full">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <img 
              src="https://media.cara.id/images/logo-cara-white.png" 
              alt="Cara Logo" 
              className="h-16"
            />
          </div>
          
          {/* Main Message */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-6">
            <h2 className="text-2xl font-bold text-white mb-3">Akses Subscription</h2>
            <p className="text-gray-400 mb-6">
              Silakan login terlebih dahulu untuk melihat paket subscription dan mengelola langganan Anda.
            </p>
            
            <button
              onClick={() => {
                // Dispatch custom event to open auth modal
                window.dispatchEvent(new CustomEvent('openAuthModal'))
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              Login Sekarang
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
          
          {/* Additional Info */}
          <p className="text-sm text-gray-500">
            Belum punya akun? Login dengan Google untuk membuat akun baru.
          </p>
        </div>
      </div>
    )
  }

  // Enhanced loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading subscription data...</p>
        </div>
      </div>
    )
  }

  // Enhanced error state with retry option
  if (error) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
        <div className="bg-bg-tertiary rounded-lg p-6 max-w-md w-full text-center border border-border-primary">
          <div className="w-12 h-12 bg-state-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-state-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-text-primary mb-2">Connection Error</h3>
          <p className="text-text-secondary text-sm mb-4">
            Unable to load subscription data. Please check your connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-brand-primary hover:bg-brand-secondary text-text-primary font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <SubscriptionErrorBoundary>
      <div className="min-h-screen bg-bg-primary">
        <SubscriptionPackages />
      </div>
    </SubscriptionErrorBoundary>
  )
}

export default SubscriptionPage